import { useMemo } from 'react';
import useAsyncFn from 'react-use/lib/useAsyncFn';

import { TOKEN_OPTIONS, useAuth } from '@spotify/confidence-core-react';
import {
  getTypeOrNull,
  useAccountSetupQuery,
} from '@spotify/confidence-graphql';

const parseAccountName = (name: string) => name.slice('accounts/'.length);

export interface Account {
  displayName: string;
  accountId: string;
}

export const useAccounts = () => {
  const { loginWithRedirect, getAccessTokenSilently } = useAuth();

  const { data, loading: isLoading } = useAccountSetupQuery({
    fetchPolicy: 'cache-first',
  });

  const user = getTypeOrNull(data?.currentUser);

  const memberships = useMemo(() => user?.accountMemberships ?? [], [user]);

  const accounts: Account[] = memberships.map(org => ({
    displayName: org.displayName,
    accountId: org.loginId,
  }));

  const selectedAccountId = user && parseAccountName(user.account);
  const selectedAccount = accounts.find(a => a.accountId === selectedAccountId);

  const [{ loading: isSwitching }, switchAccount] = useAsyncFn(
    async (accountId: string, redirectUrl: string = '/') => {
      const account = memberships.find(a => a.loginId === accountId);

      if (!account) {
        // eslint-disable-next-line no-console
        console.warn(`Account ${accountId} not found`, memberships);
        return;
      }

      const externalId = account.externalId;

      // FIXNE: would we great if all of this was hidden in useAuth.
      await getAccessTokenSilently({
        ...TOKEN_OPTIONS,
        cacheMode: 'off',
        authorizationParams: {
          ...TOKEN_OPTIONS.authorizationParams,
          organization: externalId,
        },
      });

      await loginWithRedirect({
        appState: { returnTo: redirectUrl },
        authorizationParams: { organization: externalId },
      });
    },
    [memberships, getAccessTokenSilently, loginWithRedirect],
  );

  return {
    accounts,
    isSwitching,
    isLoading,
    selectedAccountId,
    selectedAccount,
    switchAccount,
  };
};
